<script setup lang="ts">
import type { FieldRule } from '#layers/access/types/permissions/policy';
import type { GlobalCollectionId } from '#layers/data-model/public';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

interface PolicyFieldRuleFilterButtonProps {
	collectionId: GlobalCollectionId;
	followItemRules: boolean;
	mode: 'follow-item-rules' | 'custom-field-rules';
}

const { mode, followItemRules } = defineProps<PolicyFieldRuleFilterButtonProps>();
const emit = defineEmits<{
	followItemRulesChange: [follow: boolean];
}>();

const modelValue = defineModel<FieldRule>({ required: true });

const { t } = useI18n();

const color = computed(() => mode === 'follow-item-rules' ? 'neutral' : 'warning');
const icon = computed(() => mode === 'follow-item-rules' ? 'lucide:filter' : 'lucide:triangle-alert');
const buttonClass = computed(() => mode === 'follow-item-rules' ? 'text-dimmed' : '');
const tooltipText = computed(() => mode === 'follow-item-rules' ? t('policy.field_rules.filter_follow_item_rules') : t('policy.field_rules.filter_custom_field_rules'));

// TODO can this not be a defineModel?
// eslint-disable-next-line vue/no-setup-props-reactivity-loss -- seeds local editable state; later prop changes are intentionally not synced
const followRef = ref(followItemRules);
const follow = computed({
	get() {
		return followRef.value;
	},
	set(newFollow: boolean) {
		followRef.value = newFollow;

		emit('followItemRulesChange', newFollow);
	},
});

watch(() => followItemRules, (newFollowItemRules) => {
	followRef.value = newFollowItemRules;
});
</script>

<template>
	<DPopover v-if="modelValue" mode="click" :content="{ side: 'left' }" :ui="{ content: 'min-w-72' }">
		<DTooltip :text="tooltipText">
			<DButton :class="buttonClass" :icon :color variant="link" size="sm" />
		</DTooltip>

		<template #content>
			<DAnimatedSheet>
				<div class="border-b px-4 pt-4 pb-2">
					<DSwitch
						v-model="follow"
						size="sm"
						:label="t('policy.field_rules.follow_item_rules')"
						:description="t('policy.field_rules.follow_item_rules_description')"
					/>
				</div>

				<InputFilterBlock
					v-model="modelValue.filter"
					gate="schema"
					:collection-id
					:disabled="follow"
					clear-filter-display-style="button"
					hide-border
					enable-variables
				/>
			</DAnimatedSheet>
		</template>
	</DPopover>
</template>
